// React Dependencies
import React, { useEffect, useRef, useState } from 'react';

// External Dependencies
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

// Internal Dependencies
import { containerHeaderTextStyle } from '../../../../styles/common';
import PromoCodesTable from './PromoCodesTable';
import CreateModal from './CreateModal';
import MText from '../../../../components/Common/MText';
import MButton from '../../../../components/MButton';
import Colours from '../../../../styles/colours';
import BulkUpdateDeleteModal from './BulkUpdateDeleteModal';
import ApiService from '../../../../services/apiService';
import promoCodeAction from '../../../../actions/promoCode';
import BulkRedeemModal from './BulkRedeemModal';
import { Status } from '../../../../common/constants';
import { IsEmpty } from '../../../../common/checks';

const reformatDate = (dateString) => (
  moment
    .parseZone(dateString)
    .utcOffset('+08', true)
    .startOf('day')
    .format()
);

const PromoCodes = (props) => {
  const tableRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isBulkRedeemModalOpen, setIsBulkRedeemModalOpen] = useState(false);

  const onOpenBulkUpdateDeleteModal = () => {
    setIsBulkModalOpen(true);
  };

  const onCloseBulkUpdateDeleteModal = () => {
    setIsBulkModalOpen(false);
  };

  const onOpenBulkRedeemModal = () => {
    setIsBulkRedeemModalOpen(true);
  };

  const fetchPageGifts = async () => {
    try {
      const result = await ApiService.getAllGifts(props.match.params.pageId, { status: Status.Activated });
      const formatedGifts = _.map(result.rows, (row) => _.assign({ id: row.id, label: row.label }));

      props.dispatchPromoCodesGiftSet(formatedGifts);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const refreshPromoCodes = () => {
    if (!IsEmpty(tableRef.current)) {
      tableRef.current.refreshPromoCodes();
    }
  };

  const fetchPromoCodes = async (query) => {
    try {
      props.dispatchShowLoadingOverlay();

      query.pageNo = query.page;
      const { pageNo, pageSize } = query;

      const queryFilters = _.fromPairs(
        _.map(query.filters, (filter) => [
          filter.column.field,
          filter.value,
        ]),
      );

      if (!IsEmpty(queryFilters.expiryDate)) {
        queryFilters.expiryDate = reformatDate(queryFilters.expiryDate);
      }

      if (!IsEmpty(queryFilters.updatedAt)) {
        queryFilters.updatedAt = reformatDate(queryFilters.updatedAt);
      }

      if (!IsEmpty(queryFilters.createdAt)) {
        const createdDate = reformatDate(queryFilters.createdAt);
        queryFilters.createdAtStartRange = createdDate;
        queryFilters.createdAtEndRange = createdDate;
      }

      queryFilters.pageNo = pageNo;
      queryFilters.pageSize = pageSize;

      const { rows, totalCount } = await ApiService.getPromoCodes(props.match.params.pageId, queryFilters);

      return {
        data: rows,
        page: pageNo,
        totalCount,
      };
    } catch (err) {
      toast.error(err.message);
      return {
        data:       [],
        page:       0,
        totalCount: 0,
      };
    } finally {
      props.dispatchHideLoadingOverlay();
    }
  };

  const fetchPageOutlets = async () => {
    try {
      const res = await ApiService.getOutlets(props.match.params.pageId);
      setOutlets(res);
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    fetchPageOutlets();
    fetchPageGifts();
  }, []);

  return (
    <div className="container-fluid">
      <div className="mt-4 row">
        <div className="col-12 col-md-4 pl-4 pt-2">
          <MText
            variant={containerHeaderTextStyle.variant}
            bold
            size={16}
            text="Promo Codes"
          />
        </div>
        <div className="col-12 mt-2 mt-sm-0 col-md-8 pl-4">
          <div className="d-flex justify-content-sm-end">
            <MButton
              text="Bulk Redeem"
              colour={Colours.Primary}
              textColour={Colours.White}
              textSize={12}
              onClick={onOpenBulkRedeemModal}
            />
            <MButton
              className="ml-3"
              text="Bulk Update/Delete"
              colour={Colours.Primary}
              textColour={Colours.White}
              textSize={12}
              onClick={onOpenBulkUpdateDeleteModal}
            />
            <MButton
              className="ml-3"
              text="+ Upload promo codes"
              colour={Colours.Primary}
              textColour={Colours.White}
              textSize={12}
              onClick={() => setOpen(true)}
            />
          </div>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-12 pl-4">
          <PromoCodesTable
            ref={tableRef}
            fetchPromoCodes={fetchPromoCodes}
          />
        </div>
      </div>

      <CreateModal
        open={open}
        onClose={() => setOpen(false)}
        onPromoCodeUploaded={refreshPromoCodes}
        outlets={outlets}
      />

      <BulkUpdateDeleteModal
        open={isBulkModalOpen}
        onClose={onCloseBulkUpdateDeleteModal}
        pageId={props.match.params.pageId}
        onActionCompleted={refreshPromoCodes}
        fetchPromoCodes={fetchPromoCodes}
      />

      <BulkRedeemModal
        open={isBulkRedeemModalOpen}
        onClose={() => setIsBulkRedeemModalOpen(false)}
        outlets={outlets}
        onActionCompleted={refreshPromoCodes}
      />
    </div>
  );
};

PromoCodes.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      pageId: PropTypes.string,
    }),
  }).isRequired,
  promoCode: PropTypes.shape({
    promoCodes:         PropTypes.arrayOf(PropTypes.shape({})),
    gifts:              PropTypes.arrayOf(PropTypes.shape({})),
    showLoadingOverlay: PropTypes.bool,
  }).isRequired,

  dispatchPromoCodesGiftSet:  PropTypes.func.isRequired,
  dispatchShowLoadingOverlay: PropTypes.func.isRequired,
  dispatchHideLoadingOverlay: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  promoCode: state.promoCode,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchPromoCodesGiftSet:  (data) => promoCodeAction.setPromoCodesGift(dispatch, data),
  dispatchShowLoadingOverlay: () => promoCodeAction.showPromoCodeLoadingOverlay(dispatch),
  dispatchHideLoadingOverlay: () => promoCodeAction.hidePromoCodeLoadingOverlay(dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(PromoCodes);
